import React from 'react';
import Router from 'next/router';

import { gql } from '@soundxyz/gql-string';
import { createEnum, enumChecker } from '@soundxyz/utils/const';
import type { FragmentType } from '@graphql/reactQuery';
import {
  getFragment,
  SectionExpandedArtistInfoFragmentDoc,
  SectionExpandedUserInfoFragmentDoc,
} from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogError';
import { ArtistActivitySection } from './ArtistActivity/ArtistActivity';
import { ArtistAppearsOnSection } from './ArtistAppearsOn';
import { ArtistsBackedSection } from './ArtistsBacked';
import { CollectionSection } from './Collection';
import { CollectorActivitySection } from './CollectorActivity/CollectorActivity';
import { CollectorAppearsOnSection } from './CollectorAppearsOn';
import { CollectorsSection } from './Collectors';
import { PlaylistsSection } from './Playlists';
import { ReleasesSection } from './Releases';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment SectionExpandedArtistInfo on Artist {
    id
    ...CollectorsSectionArtistInfo
    ...ReleasesSectionArtistInfo
    ...ArtistAppearsOnSectionArtistInfo
    ...ArtistActivitySectionArtistInfo
  }

  fragment SectionExpandedUserInfo on User {
    id
    ...PlaylistsSectionUserInfo
    ...CollectorAppearsOnUserInfo
    ...CollectorActivityUserInfo
    ...CollectionSectionUserInfo
    ...BackedArtistSection
  }
`);

export const ProfileSectionType = createEnum([
  'playlists',
  'releases',
  'appears-on',
  'collectors',
  'collection',
  'activity',
  'artists-backed',
]);
export type ProfileSectionType = (typeof ProfileSectionType)[keyof typeof ProfileSectionType];

export const isProfileSectionType = enumChecker(ProfileSectionType);

export const SectionExpanded = ({
  profileSection,
  user,
  artist: artistFragment,
}: {
  profileSection: ProfileSectionType;
  user: FragmentType<SectionExpandedUserInfoFragmentDoc>;
  artist: FragmentType<SectionExpandedArtistInfoFragmentDoc> | null;
}) => {
  const logError = useLogError();

  const userInfo = getFragment(SectionExpandedUserInfoFragmentDoc, user);

  const { id: userId } = userInfo;

  const artist = getFragment(SectionExpandedArtistInfoFragmentDoc, artistFragment);

  switch (profileSection) {
    case 'playlists': {
      return <PlaylistsSection user={userInfo} />;
    }
    case 'releases': {
      if (!artist) {
        logError({ error: new Error('No artist ID') });
        return null;
      }
      return <ReleasesSection artist={artist} />;
    }
    case 'appears-on': {
      if (artist) {
        return <ArtistAppearsOnSection artist={artist} />;
      } else if (userId && !artist) {
        return <CollectorAppearsOnSection user={userInfo} />;
      } else {
        logError({ error: new Error('No artist ID and no user ID') });
        return null;
      }
    }
    case 'collectors': {
      if (!artist) {
        logError({ error: new Error('No artist ID') });
        return null;
      }

      return <CollectorsSection artist={artist} />;
    }
    case 'collection': {
      return <CollectionSection user={userInfo} />;
    }
    case 'activity': {
      if (artist) {
        return <ArtistActivitySection artist={artist} />;
      } else if (userId && !artist) {
        return <CollectorActivitySection user={userInfo} />;
      } else {
        logError({ error: new Error('No artist ID and no user ID') });
        return null;
      }
    }
    case 'artists-backed': {
      if (artist) {
        logError({ error: new Error('No artist allowed') });
        return null;
      }
      return <ArtistsBackedSection user={userInfo} />;
    }
    default:
      Router.push('/404');
      return null;
  }
};
